
import { useState, useEffect } from 'react';

export default function Exercises() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState(''); // New: Filter by muscle group
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // For pagination

  // Unique muscle groups for dropdown (computed once)
  const [muscleGroups, setMuscleGroups] = useState([]);

  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const response = await fetch(
          'https://raw.githubusercontent.com/yuhonas/free-exercise-db/main/dist/exercises.json'
        );
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        setExercises(data);

        // Extract unique muscle groups
        const allMuscles = new Set();
        data.forEach(ex => {
          ex.primaryMuscles?.forEach(m => allMuscles.add(m));
          ex.secondaryMuscles?.forEach(m => allMuscles.add(m));
        });
        setMuscleGroups(Array.from(allMuscles).sort());

        setLoading(false);
      } catch (err) {
        setError('Failed to load exercises. Try refreshing or check connection.');
        setLoading(false);
      }
    };

    fetchExercises();
  }, []);

  // Reset page on search or filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedMuscle]);

  const filtered = exercises.filter(ex => {
    const query = search.toLowerCase();
    const muscleMatch = !selectedMuscle || 
      ex.primaryMuscles?.includes(selectedMuscle) || 
      ex.secondaryMuscles?.includes(selectedMuscle);

    return muscleMatch && (
      ex.name?.toLowerCase().includes(query) ||
      ex.force?.toLowerCase().includes(query) ||
      ex.level?.toLowerCase().includes(query) ||
      ex.mechanic?.toLowerCase().includes(query) ||
      ex.equipment?.toLowerCase().includes(query) ||
      ex.category?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  if (loading) return <div className="text-center py-10 text-white">Loading exercises...</div>;
  if (error) return <div className="text-center py-10 text-red-400">{error}</div>;

  return (
    <section className="py-8 max-w-6xl mx-auto px-5">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">Krio</span>
           Exercise Library
        </h2>
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search by name, type, level, equipment..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 bg-surface-dark border border-slate-700 rounded-full px-4 py-2 text-white placeholder-slate-500 focus:border-primary outline-none"
          />
          <select
            value={selectedMuscle}
            onChange={(e) => setSelectedMuscle(e.target.value)}
            className="w-full sm:w-48 bg-surface-dark border border-slate-700 rounded-full px-4 py-2 text-white focus:border-primary outline-none"
          >
            <option value="">Filter by Muscle</option>
            {muscleGroups.map((muscle, i) => (
              <option key={i} value={muscle}>{muscle}</option>
            ))}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-slate-400 py-10">No exercises found. Try a different search or filter.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginated.map((ex, i) => (
              <ExerciseCard key={i} ex={ex} />
            ))}
          </div>

          {/* Pagination Controls - Responsive */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className="w-full sm:w-auto bg-surface-dark text-primary px-6 py-3 rounded-full border border-slate-700 disabled:opacity-50 hover:bg-slate-800 transition"
            >
              Previous
            </button>
            <span className="text-slate-300 text-center">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto bg-surface-dark text-primary px-6 py-3 rounded-full border border-slate-700 disabled:opacity-50 hover:bg-slate-800 transition"
            >
              Next
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ExerciseCard Component (with truncated instructions ~50-60 words)
function ExerciseCard({ ex }) {
  const [showFullInstructions, setShowFullInstructions] = useState(false);

  const toggleInstructions = () => {
    setShowFullInstructions(prev => !prev);
  };

  // Truncate instructions to ~50-60 words
  const fullInstructions = ex.instructions?.join(' ') || '';
  const truncated = fullInstructions.split(' ').slice(0, 60).join(' ') + (fullInstructions.split(' ').length > 60 ? '...' : '');

  return (
    <div className="bg-surface-dark border border-slate-800 rounded-xl p-4 hover:border-primary/50 transition-all flex flex-col">
      <div className="flex items-start gap-4 mb-3">
        <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-primary text-3xl">Krio</span>
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-white capitalize mb-1 text-base sm:text-lg">{ex.name}</h3>
          <p className="text-slate-400 text-xs sm:text-sm">
            Primary: <span className="text-primary">{ex.primaryMuscles?.join(', ') || '—'}</span>
          </p>
          <p className="text-slate-400 text-xs sm:text-sm">
            Secondary: <span className="text-primary">{ex.secondaryMuscles?.join(', ') || '—'}</span>
          </p>
          <p className="text-slate-500 text-xs mt-1">Category: {ex.category || '—'}</p>
          <p className="text-slate-500 text-xs">Level: {ex.level || '—'}</p>
          <p className="text-slate-500 text-xs">Equipment: {ex.equipment || 'Bodyweight'}</p>
          <p className="text-slate-500 text-xs">Force: {ex.force || '—'}</p>
          {/* <p className="text-slate-500 text-xs">Mechanic: {ex.mechanic || '—'}</p> */}
        </div>
      </div>

      {/* Truncated Instructions */}
      {fullInstructions && (
        <div className="mb-4 flex-grow">
          <h4 className="text-sm font-bold text-white mb-2">Instructions:</h4>
          <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
            {showFullInstructions ? fullInstructions : truncated}
          </p>
          {fullInstructions.split(' ').length > 60 && (
            <button
              onClick={toggleInstructions}
              className="text-primary text-xs mt-2 hover:underline"
            >
              {showFullInstructions ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>
      )}

      {/* Images - Horizontal scroll, responsive */}
      {ex.images?.length > 0 && (
        <div className="overflow-x-auto flex gap-2 pb-2 scrollbar-hide snap-x snap-mandatory mt-auto">
          {ex.images.map((imgPath, j) => (
            <img
              key={j}
              src={`https://raw.githubusercontent.com/yuhonas/free-exercise-db/main/exercises/${imgPath}`}
              alt={`${ex.name} ${j + 1}`}
              className="w-40 sm:w-48 h-24 sm:h-32 object-cover rounded-lg flex-shrink-0 snap-center"
              loading="lazy"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/192x128?text=Image+Not+Found';
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}